import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { api } from "../../lib/api"
import type { Compra, Desempenho, GatewayDePagamento, Produto } from "../../types"
import { prazo, reais } from "../../lib/format"
import { listar } from "../../lib/texto"
import { Botao, Card, Carregando, Erro } from "../ui"
import { ofertaEmpilhada, promessaDaOferta } from "../Resultado/Preparacao"
import BotaoCartao, { LinhaParcelada } from "./BotaoCartao"
import Pix from "./Pix"
import Renovacao from "./Renovacao"
import RetornoDoCartao, { type EstadoDoRetorno } from "./RetornoDoCartao"

export default function Acesso() {
  const [produtos, setProdutos] = useState<Produto[] | null>(null)
  const [gateways, setGateways] = useState<GatewayDePagamento[]>([])
  const [compra, setCompra] = useState<Compra | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [fracas, setFracas] = useState<string[]>([])
  // "Pagar de outro jeito" num cartão em aberto: o botão volta a oferecer o
  // Pix em vez de reabrir a mesma tela de espera.
  const [cartaoDescartado, setCartaoDescartado] = useState(false)
  const [parametros] = useSearchParams()
  const navegar = useNavigate()

  const slugPreferido = parametros.get("habilitacao")
  // A volta da página do cartão: `?status=sucesso|pendente|falha&compra=N`.
  // O status é só o que o navegador trouxe; quem confirma é o webhook.
  const estadoDoRetorno = parametros.get("status") as EstadoDoRetorno
  const compraDoRetorno = parametros.get("compra")

  useEffect(() => {
    if (!compraDoRetorno) return
    api
      .get<Compra>(`/compras/${compraDoRetorno}`)
      .then(setCompra)
      // Compra que não é desta pessoa, ou id inventado: segue para a oferta.
      .catch(() => {})
  }, [compraDoRetorno])

  // A tela de resultado nomeia as unidades em que a pessoa perdeu ponto, e até
  // agora a promessa se dissolvia num checkout genérico. Reaproveita o mesmo
  // endpoint do painel em vez de carregar as unidades na URL: assim a
  // personalização também vale para quem volta pelo histórico dias depois.
  useEffect(() => {
    if (!slugPreferido) return

    api
      .get<Desempenho>(`/me/desempenho?habilitacao=${encodeURIComponent(slugPreferido)}`)
      .then((d) => {
        setFracas(
          d.por_unidade
            .filter((u) => u.total > 0 && u.aproveitamento < 50)
            .sort((a, b) => a.aproveitamento - b.aproveitamento)
            .slice(0, 3)
            .map((u) => u.unidade)
        )
      })
      // Personalização é enfeite: se falhar, o checkout segue genérico. Nunca
      // pode ser o motivo de alguém não conseguir pagar.
      .catch(() => {})
  }, [slugPreferido])

  useEffect(() => {
    // `tela=acesso` identifica esta tela para o evento de funil `view_item` —
    // o Resultado busca o mesmo endpoint só para exibir o preço, e não conta.
    const consulta = slugPreferido
      ? `/produtos?tela=acesso&habilitacao=${encodeURIComponent(slugPreferido)}`
      : "/produtos?tela=acesso"
    api.get<{ produtos: Produto[]; gateways: GatewayDePagamento[] }>(consulta).then((r) => {
      setProdutos(r.produtos)
      setGateways(r.gateways)
      // Veio para a habilitação que já tem um pagamento em aberto (Pix no
      // prazo, ou cartão cuja preferência está de pé): retoma ele. É para
      // onde o card do painel manda quem gerou e não pagou — e onde cai quem
      // voltou do cartão sem os parâmetros (navegador embutido).
      const pendente = r.produtos.find((p) => p.slug === slugPreferido)?.compra_pendente
      if (pendente && !compraDoRetorno) setCompra(pendente)
    })
  }, [])

  if (!produtos) return <Carregando />
  if (compra?.gateway === "mercado_pago") {
    return (
      <RetornoDoCartao
        compra={compra}
        estado={estadoDoRetorno}
        aoLiberar={() => navegar("/painel")}
        aoPagarDeOutroJeito={() => {
          setCartaoDescartado(true)
          setCompra(null)
          navegar(`/acesso${slugPreferido ? `?habilitacao=${slugPreferido}` : ""}`)
        }}
      />
    )
  }
  if (compra) return <Pix compra={compra} aoLiberar={() => navegar("/painel")} aoVoltar={() => setCompra(null)} />

  // A escada: o produto desta habilitação primeiro; se ele for o Motonauta
  // avulso, a preparação completa do Arrais logo abaixo (quem vai pilotar
  // barco também faz uma prova só, a de Arrais); o resto depois.
  const peso = (p: Produto) => (p.slug === slugPreferido ? 0 : p.pacote ? 1 : 2)
  const ordenados = [...produtos].sort((a, b) => peso(a) - peso(b))

  // Quem já foi cliente desta habilitação não "começa" nada: a página vira a
  // de renovação, com o progresso que a pessoa construiu e o preço de renovar.
  const renovar = slugPreferido ? produtos.find((p) => p.slug === slugPreferido && p.renovacao && !p.acesso_vigente) : undefined
  if (renovar && gateways.includes("asaas")) {
    return <Renovacao produto={renovar} aoCriar={setCompra} />
  }

  // O pagamento em aberto que o botão retoma — o cartão descartado não conta.
  function pendenteDe(produto: Produto): Compra | null {
    const c = produto.compra_pendente
    if (!c || (c.gateway === "mercado_pago" && cartaoDescartado)) return null
    return c
  }

  // O que o botão principal diz: retoma o pagamento em aberto, ou abre o Pix.
  function rotuloDoPix(produto: Produto): string {
    if (produto.acesso_vigente) return "Já liberado"
    const c = pendenteDe(produto)
    if (c?.gateway === "mercado_pago") return "Ver meu pagamento"
    if (c) return "Ver meu Pix"
    return "Pagar com Pix"
  }

  return (
    <div className="space-y-5">
      <div>
        {/* A manchete é a preparação; acesso, duração e forma de pagamento são
            detalhe, ao lado do preço. "6 meses de acesso a todos os simulados"
            vendia estoque — e estoque de simulado o nicho dá de graça. */}
        <h1 className="text-xl font-bold text-navy-900">Começar minha preparação</h1>
        <p className="text-sm text-slate-600">
          Descubra o que ainda falta estudar, treine no formato da prova e marque a prova sabendo que
          vai passar.
        </p>
      </div>

      {erro && <Erro mensagem={erro} />}

      {ordenados.map((produto) => (
        <Card key={produto.id} className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-bold text-navy-900">{produto.pacote ? produto.nome : produto.habilitacao}</h2>
              {produto.acesso_vigente ? (
                <p className="text-sm text-emerald-700">
                  Acesso liberado — faltam {produto.dias_restantes} dias.
                </p>
              ) : fracas.length > 0 && produto.slug === slugPreferido ? (
                <p className="text-sm text-slate-600">
                  Suas unidades mais fracas hoje:{" "}
                  <strong className="font-semibold text-navy-900">{listar(fracas)}</strong>.
                </p>
              ) : produto.pacote ? (
                <p className="text-sm text-slate-600">{promessaDaOferta(produto)}</p>
              ) : (
                <p className="text-sm text-slate-600">
                  Para quem vai pilotar só moto aquática. Vai tirar Arrais também? Então você faz uma prova só, a de
                  Arrais — e a preparação completa já inclui o Motonauta.
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-2xl font-extrabold text-navy-900">{reais(produto.preco_centavos)}</p>
              <p className="text-xs text-slate-500">
                {produto.renovacao ? "renovação · " : "pagamento único · "}
                {prazo(produto.meses_de_acesso)} · sem assinatura
              </p>
              {/* Parcela não é mensalidade: o preço cheio fica em cima, e isto
                  é como pagar — só com o cartão ligado no servidor. */}
              <LinhaParcelada produto={produto} className="mt-0.5" />
              {/* "de/por" só com relâmpago vigente: o riscado é o preço praticado,
                  e o prazo é do servidor (ver Relampago no resultado). */}
              {produto.relampago_ate && produto.preco_depois_centavos !== null && (
                <p className="mt-1 text-xs font-semibold text-gold-700">
                  <s className="font-normal text-slate-500">de {reais(produto.preco_depois_centavos)}</s> · promoção até{" "}
                  {new Date(produto.relampago_ate).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit" })}
                </p>
              )}
            </div>
          </div>

          {/* A preparação completa vem empilhada em três blocos (ver
              ofertaEmpilhada); o avulso, com os quatro pilares de sempre. */}
          {produto.pacote ? (
            ofertaEmpilhada(produto, null).map((bloco) => (
              <div key={bloco.titulo}>
                <p className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-gold-600">{bloco.titulo}</p>
                <ul className="mt-1.5 space-y-1.5 text-sm text-slate-700">
                  {bloco.itens.map(([titulo, texto]) => (
                    <li key={titulo}>
                      <strong className="text-navy-900">{titulo}:</strong> {texto}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          ) : (
            <ul className="space-y-1.5 text-sm text-slate-700">
              <li><strong className="text-navy-900">Diagnóstico por unidade</strong> a cada simulado: descubra exatamente o que está derrubando sua nota.</li>
              <li><strong className="text-navy-900">Estudo pelos erros:</strong> cada questão com a resposta certa, o comentário e a norma citada (NORMAM, LESTA, RIPEAM).</li>
              <li><strong className="text-navy-900">Treino no formato da prova:</strong> o mesmo número de questões, o mesmo tempo e a mesma nota de corte do exame da Capitania.</li>
              <li><strong className="text-navy-900">Saber quando está pronto:</strong> pronto é passar duas vezes seguidas com 7,0 ou mais. Aí marque a prova sabendo que vai passar.</li>
            </ul>
          )}

          {/* Não é promessa nova: o art. 49 do CDC dá sete dias para desistir de
              compra online, e os termos já detalhavam isso desde sempre. O que
              faltava era dizer na hora de pagar — direito que ninguém conhece
              não tira o medo de ninguém. Deliberadamente "desistir", e nunca
              "garantia de aprovação", que seria falsa e é recusada nos termos. */}
          <p className="text-xs text-slate-500">
            Sete dias para desistir, com reembolso integral e sem justificar.
          </p>

          <div className="flex flex-wrap gap-2">
            <Botao
              onClick={() => {
                // Já existe um pagamento em aberto: mostra ele, não um formulário novo.
                const c = pendenteDe(produto)
                if (c) {
                  setCompra(c)
                  return
                }
                // O marco da intenção: o Pix (`begin_checkout`) só conta depois
                // de o gateway responder. Dispara e não espera — medir nunca
                // pode segurar a navegação.
                api
                  .post<unknown>("/eventos", { nome: "clique_pagar", habilitacao: produto.slug, gateway: "asaas" })
                  .catch(() => {})
                navegar(`/acesso?habilitacao=${produto.slug}&pagar=${produto.id}`)
              }}
              disabled={produto.acesso_vigente}
            >
              {rotuloDoPix(produto)}
            </Botao>
            {/* O cartão só aparece se o servidor estiver oferecendo
                (`produto.parcelas`) — ver BotaoCartao. */}
            {!produto.acesso_vigente && !pendenteDe(produto) && (
              <BotaoCartao produto={produto} aoFalhar={setErro} />
            )}
          </div>

          {parametros.get("pagar") === String(produto.id) && !produto.acesso_vigente && (
            <FormularioPix produto={produto} aoCriar={setCompra} aoFalhar={setErro} />
          )}
        </Card>
      ))}

      <p className="pb-6 text-center text-xs text-slate-500">
        Você paga uma vez e estuda durante todo o período. Não há cobrança recorrente.
      </p>
    </div>
  )
}

// O formulário do Pix. Com a chave Pix da conta configurada no servidor
// (`produto.pix_pede_cpf` falso), o QR estático nasce assim que o formulário
// abre — um clique, um QR, sem documento. Sem a chave, pede o CPF que a
// cobrança com cliente exige; ele vai para o gateway e não é guardado no nosso
// banco. Exportado porque a tela de resultado abre o mesmo formulário no
// lugar, sem trocar de página.
export function FormularioPix({
  produto,
  aoCriar,
  aoFalhar,
  layout = "caixa",
}: {
  produto: Produto
  aoCriar: (compra: Compra) => void
  aoFalhar: (mensagem: string) => void
  /** `linha`: campo e botão lado a lado, no mesmo espaço que um botão ocupava — para abrir no lugar, sem mover o resto. */
  layout?: "caixa" | "linha"
}) {
  const pedeCpf = produto.pix_pede_cpf
  const [cpf, setCpf] = useState("")
  // Sem CPF o Pix nasce na montagem: já começa "enviando" para não piscar um
  // botão antes de o primeiro request sair.
  const [enviando, setEnviando] = useState(!pedeCpf)
  const cpfAvisado = useRef(false)

  // O passo entre o clique em pagar e o Pix era cego: quem sumia aqui tanto
  // podia ter desistido ao ver o campo quanto depois de preenchê-lo. Avisa uma
  // vez, quando o CPF fica completo — e só que ficou: o número não sai daqui
  // senão para o `POST /compras`. Dispara e não espera, como o `clique_pagar`.
  function mudarCpf(valor: string) {
    setCpf(valor)
    if (cpfAvisado.current || valor.replace(/\D/g, "").length !== 11) return
    cpfAvisado.current = true
    api.post<unknown>("/eventos", { nome: "cpf_digitado", habilitacao: produto.slug, gateway: "asaas" }).catch(() => {})
  }

  async function gerar(evento?: React.FormEvent) {
    evento?.preventDefault()
    setEnviando(true)
    try {
      const corpo = pedeCpf
        ? { produto_id: produto.id, gateway: "asaas", cpf }
        : { produto_id: produto.id, gateway: "asaas" }
      aoCriar(await api.post<Compra>("/compras", corpo))
    } catch (e) {
      aoFalhar(e instanceof Error ? e.message : "Não foi possível gerar o Pix.")
    } finally {
      setEnviando(false)
    }
  }

  useEffect(() => {
    if (!pedeCpf) void gerar()
    // Só na montagem — depois quem manda é o botão de tentar de novo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!pedeCpf) {
    return enviando ? (
      <Carregando texto="Gerando o Pix…" />
    ) : (
      <Botao variante="compra" onClick={() => gerar()}>
        Tentar de novo
      </Botao>
    )
  }

  const valido = cpf.replace(/\D/g, "").length === 11

  if (layout === "linha") {
    return (
      <>
        {/* O porquê vem ANTES do campo. Ele morava embaixo do botão, e no
            celular o `autoFocus` abre o teclado por cima: a pessoa via um campo
            pedindo documento sem nenhum motivo à vista. Curta e com largura
            travada — é ela que dita a largura do slot, e linha comprida aqui
            empurra o texto do card para o lado. */}
        <p className="mb-2 max-w-[19rem] text-xs text-slate-500">
          O Pix exige o CPF de quem paga. Ele vai para o banco, não fica com a gente.
        </p>
        <form onSubmit={gerar} className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
          <input
            aria-label="CPF"
            value={cpf}
            onChange={(e) => mudarCpf(e.target.value)}
            inputMode="numeric"
            autoFocus
            placeholder="Seu CPF"
            className="w-full rounded-xl border border-navy-200 bg-white px-4 py-3 text-sm sm:w-40"
          />
          <Botao type="submit" variante="compra" disabled={enviando || !valido} className="whitespace-nowrap">
            {enviando ? "Gerando…" : "Gerar Pix"}
          </Botao>
        </form>
      </>
    )
  }

  return (
    <form onSubmit={gerar} className="space-y-3 rounded-xl bg-navy-50 p-4">
      <div>
        <label htmlFor={`cpf-${produto.id}`} className="block text-sm font-semibold text-navy-900">
          CPF
        </label>
        <input
          id={`cpf-${produto.id}`}
          value={cpf}
          onChange={(e) => mudarCpf(e.target.value)}
          inputMode="numeric"
          placeholder="000.000.000-00"
          className="mt-1 w-full rounded-xl border border-navy-200 px-3 py-2.5 text-sm"
        />
        <p className="mt-1 text-xs text-slate-500">Exigido pelo Pix. Vai para o banco, não fica com a gente.</p>
      </div>
      <Botao type="submit" variante="compra" disabled={enviando || !valido}>
        {enviando ? "Gerando…" : "Gerar Pix"}
      </Botao>
    </form>
  )
}
